import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ApiError, loginRequest } from '../lib/api';
import { useAuthStore } from '../lib/auth';

interface LocationState {
  readonly from?: Location;
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const data = await loginRequest(username, password);
      setAuth(data.user, data.csrfToken, [...data.permissions]);
      const state = location.state as LocationState | null;
      const target = state?.from?.pathname ?? '/agents';
      navigate(target, { replace: true });
    } catch (err) {
      const fallback = 'Login failed. Please try again.';
      setError(err instanceof ApiError ? (err.messages[0]?.message ?? fallback) : fallback);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={3}
        sx={{ p: 4, width: '100%', maxWidth: 400 }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" component="h2">
            Operator sign in
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the account provided by your administrator.
          </Typography>

          <TextField
            label="Username"
            type="text"
            name="username"
            autoComplete="username"
            required
            disabled={submitting}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            disabled={submitting}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            size="small"
          />

          {error !== null ? <Alert severity="error">{error}</Alert> : null}

          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
